import type React from "react";

import type { MessageV2Dto } from "@/api/types";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { Button } from "../../../components/Button/Button";

interface InactivePostWarningProps {
  message: MessageV2Dto;
  onClose: () => void;
}

export function InactivePostWarning({ message, onClose }: InactivePostWarningProps): React.ReactNode {
  const slug = useSlug();
  const hasPermission = usePermission();
  const { t } = useTranslation();

  const canCreatePost = hasPermission((x) => x.canCreatePosts);

  return (
    <p className="flex max-w-sm flex-col gap-2">
      <span className="text-caption-bold">{t("component.community-post.comments.old-post-warning")}</span>
      <span>
        {canCreatePost
          ? t("component.community-post.comments.old-post-warning.description")
          : t("component.community-post.comments.old-post-warning.description.no-permission")}
      </span>
      {canCreatePost && (
        <Button
          type="link"
          href={
            hasPermission((x) => x.isAdmin)
              ? routes.messageFeed.adminCreatePost({ slug })
              : routes.messageFeed.list({ slug })
          }
          onClick={onClose}
          styling="ghostPrimary"
          className="text-caption-bold text-white underline"
          state={{
            residentCreatePostType: message.messageLabel,
            groupId: message.group?.id,
            isGeneral: message.group == null,
          }}
        >
          {t("component.community-post.comments.old-post-warning.create-new")}
        </Button>
      )}
      {!canCreatePost && (
        <Button
          type="button"
          onClick={onClose}
          styling="ghostPrimary"
          className="text-caption-bold text-white underline"
        >
          {t("common.action.cancel")}
        </Button>
      )}
    </p>
  );
}
